import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    RootState,
    setScriptMessage,
    setPseudoMessage,
    setSessionId,
    setProcessing,
    setActiveSessionId,
    setSessionMode,
    selectEffectiveAnalytics,
    changeSetting
} from '../../utils/store';
import { Card, Button, Badge, makeStyles, Spinner, Text,
    Image
 } from '@fluentui/react-components';
import { Edit20Regular, DataUsageToolboxRegular } from '@fluentui/react-icons';
import { UserContext, useHasDebugAccess } from '../../utils/UserContext';
import Message from './Message';
import TextMessage from './TextMessage';
import AnalyticsMessage from './AnalyticsMessage';
import ReasoningAccordion from './ReasoningAccordion';
import { ChatBundle, samplePrompt } from '../../Service/ServerAPI';
import { groupMessagesWithIndex } from './GroupMessages';
import TextInput from './TextInput';
import Logo from '../../Logo.png'
import WelcomeScreen from './WelcomeScreen';
import QueryPopUp from './PromptPopUp';
import NotificationBanner from '../../main/NotificationBanner';
import StatusBar from '../../main/StatusBar';
import {
    CopilotService, sampleQueries
} from '../../Service/ServerAPI';
import { useTokenService } from '../../tokenService'; // Import your token service hook

const applyAutoCorrections = (text: string): string => {
    const bigIdRegex = /\b[0-9BCDFGHJ][0-9BCDFGHJKLMNPQRSTVWXZ]{11}\b/gi;
    return text.replace(bigIdRegex, (match) =>
        match === match.toUpperCase() ? match : match.toUpperCase()
    );
};

const useStyles = makeStyles({
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: '500px',
        position: 'relative',
        background: 'transparent',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '16px 0',
        boxShadow: 'none',
    },
    chatContainer: {
        flexGrow: 1,
        overflow: 'auto',
        padding: '16px',
        height: 'calc(60vh - 125px)',
        position: 'relative',
        '::-webkit-scrollbar': {
            width: '6px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    inputContainer: {
        padding: '3px 5px',
        flexShrink: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        overflow: 'visible',
    },
    textArea: {
        width: '100%',
        backgroundColor: '#3E434B40',
        color: '#fff',
        border: '1px solid #4D4F51',
    },
    sessionContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100px',
        border: '1px solid lightgray',
        borderRadius: '4px',
    },
    welcomeScreen: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100px',
        border: '1px solid lightgray',
        borderRadius: '4px',
    },
    welcomeTitleText: {
        marginTop: '5px',
        marginBottom: '10px',
        cursor: 'default',
        textAlign: 'center',
    },
    container: {
        gap: '5px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // border: '1px solid lightgray', 
        borderRadius: '4px',
        margin: '75px',
        marginTop: '100px'
    },
    bubble: {
        width: '30%',
        height: '100px',
        padding: '10px',
        gap: '9px',
        borderRadius: '4px',
        border: '1px solid lightgray',
        opacity: '0px',
        flexDirection: 'column',
    },
    bubbleContent: {

    },
    buttonContainerStyle: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    buttonStyle: {
        padding: '5px 10px',
        backgroundColor: 'transparent',
        color: '#ffffffc7',
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
    },
    fadeIn: {
        opacity: 1,
        transition: 'opacity 1.1s',
    },
    notVisible: {
        opacity: 0
    },
    datasetBadge: {
        display: 'flex',
        justifyContent: 'center',
        padding: '4px 0',
    },
});

interface ChatUIProps {
    sendMessage: (segmentDef: string, currentPseudocode: string) => void;
    loadSession: (sessionId: string) => void;
    cloneSession: () => void;
    onLuaScriptChange: (code: string) => void;
    isPseudocode: boolean;
    showScreen: boolean;
    setShowScreen: (showScreen: boolean) => void;
    isAuthed: boolean;
    loadingSessionId?: string | null;
    onCancelLoadSession?: () => void;
    onRetryAnalytics?: (message: any, validationReasoning: string) => void;
}

const ChatUI: React.FC<ChatUIProps> = ({
    sendMessage,
    loadSession,
    cloneSession,
    onLuaScriptChange,
    isPseudocode,
    showScreen,
    setShowScreen,
    isAuthed,
    loadingSessionId,
    onCancelLoadSession,
    onRetryAnalytics,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [input, setInput] = useState('');
    const user = useContext(UserContext);
    const hasDebugAccess = useHasDebugAccess();
    const settings = useSelector((state: RootState) => state.settings);
    const effectiveAnalytics = selectEffectiveAnalytics(settings);
    const [showBanner, setShowBanner] = React.useState(false);
    const [bannerMsg, setBannerMsg] = React.useState('');
    const [bannerId, setBannerId] = React.useState('');
    const [notificationType, setNotificationType] = React.useState<'banner' | 'statusBar'>('banner');
    const [notificationSeverity, setNotificationSeverity] = React.useState<'warning' | 'info'>('info');
    const [hardcoded, setHardcoded] = useState<boolean>(false);
    const [isPromptsReady, setPromptsReady] = useState<boolean>(false); // whether the sample prompts have been loaded from the server

    // This is what the UX loads up before the server responds with the actual sample queries
    const defaultSampleQueries: sampleQueries = {
        samplePrompts: [],
        isActivision: false
    };


    const [inputSampleQueries, setSampleQueries] = useState<sampleQueries>(defaultSampleQueries);
    const tokenService = useTokenService();
    const copilotService = CopilotService.getInstance(tokenService);
    const toggleVisibility = () => {
        setShowScreen(!showScreen);
    };
    const handleSend = () => {
        if (input.trim() !== '') {
            sendMessage(applyAutoCorrections(input), '');
            dispatch(setProcessing(true));
            setInput('');
            setShowScreen(false);
            setHardcoded(false);
        }

    };

    const handleDone = () => {
        sendMessage('Done', '');
        dispatch(setProcessing(true));
        setShowScreen(false);
        setHardcoded(false);
    };

    const handleRefresh = () => {
        dispatch(setScriptMessage([]));
        dispatch(setPseudoMessage([]));
        dispatch(setSessionId(''));
        dispatch(setSessionMode(''));
    };

    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'start',
        });
    }, [settings.pseudocodeChat, settings.chat, settings.recentReasoningNotifications]);

    React.useEffect(() => {
        copilotService.GetLatestMessage(settings.apiEndpoint)
            .then((response) => {
                if (response) {
                    if (response.showMessage) {
                        setShowBanner(true);
                        setBannerMsg(response.message);
                        setBannerId(response.id ?? '');
                        setNotificationType(response.type ?? 'banner');
                        setNotificationSeverity(response.severity ?? 'info');
                    }
                }
            });

    }, []);

    // Retrieves sample prompts from back-end, sets the `prompts` variable used by
    // both the welcome screen and the sample queries popup
    React.useEffect(() => {
        const dataset = settings.sessionDataset || settings.dataset;
        copilotService.GetSamplePrompts(settings.apiEndpoint, dataset)
            .then((response) => {
                if (response) {
                    setSampleQueries(response);
                    setPromptsReady(true);
                }
            });
    }, [settings.dataset]);

    const [isOpen, setIsOpen] = useState(false);
    const togglePopup = () => {
        setIsOpen(!isOpen);
    };

    const handleSamplePromptClick = (description: string, index: number) => {
        if (index === 0 && !isAuthed) {
            setHardcoded(true);
            sendMessage(description, '');
            dispatch(setProcessing(true));
            setShowScreen(false);
        }
        else {
            setInput(description);
            setHardcoded(false);
        }
        if (index === 10) {
            togglePopup();
        }
    };

    const { grouped, indexedMessages } = groupMessagesWithIndex(settings.chat);
    
    // Create a temporary reasoning bundle for the last message while processing
    let messagesWithLiveReasoning = grouped;
    let processingMessageIndex = -1;
    
    if (settings.isProcessing) {
        if (settings.recentReasoningNotifications.length > 0) {
            // SignalR is working - show real-time reasoning
            if (grouped.length > 0) {
                // Clone the grouped messages and add live reasoning to the last one
                messagesWithLiveReasoning = [...grouped];
                const lastMessage = { ...messagesWithLiveReasoning[messagesWithLiveReasoning.length - 1] };
                
                // Add temporary reasoning to the last message
                lastMessage.reasoning = {
                    role: 'AssistantReasoning',
                    sessionId: settings.sessionId,
                    message: settings.recentReasoningNotifications,
                    endpoint: 'hub',
                };
                
                messagesWithLiveReasoning[messagesWithLiveReasoning.length - 1] = lastMessage;
                processingMessageIndex = messagesWithLiveReasoning.length - 1;
            } else {
                // No messages yet, create a placeholder message just for reasoning
                const tempReasoningMessage: ChatBundle = {
                    reasoning: {
                        role: 'AssistantReasoning',
                        sessionId: settings.sessionId,
                        message: settings.recentReasoningNotifications,
                        endpoint: 'hub',
                    }
                };
                messagesWithLiveReasoning = [tempReasoningMessage];
                processingMessageIndex = 0;
            }
        } else if (grouped.length > 0) {
            // SignalR not working but we have messages - the Message component will handle fallback
            processingMessageIndex = grouped.length - 1;
        } else {
            // No messages and no SignalR - create a fallback processing message
            const fallbackProcessingMessage: ChatBundle = {
                reasoning: {
                    role: 'AssistantReasoning',
                    sessionId: settings.sessionId,
                    message: ['Processing your request...'],
                    endpoint: 'hub',
                }
            };
            messagesWithLiveReasoning = [fallbackProcessingMessage];
            processingMessageIndex = 0;
        }
    }
    
    // Show badge for the active dataset
    const activeDataset = settings.sessionDataset || settings.dataset;
    const datasetDisplayName = settings.availableDatasets.find(d => d.name === activeDataset)?.displayName || activeDataset;

    return (
        <Card className={classes.card}>
            <div className={classes.chatContainer}>
                {!showScreen && datasetDisplayName && (
                    <div className={classes.datasetBadge}>
                        <Badge appearance="filled" color="informative">
                            {datasetDisplayName}
                        </Badge>
                    </div>
                )}
                {showBanner && notificationType === 'banner' && <NotificationBanner message={bannerMsg} id={bannerId} severity={notificationSeverity}></NotificationBanner>}
                {showScreen && (<div className={classes.container}>
                    <Image
                        style={{ marginTop: '4px' }}
                        width={35}
                        height={50}
                        alt="logo"
                        src={Logo}
                    />
                    {loadingSessionId ? (
                        <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <Spinner size="large" />
                            <Text size={500} style={{ fontStyle: 'italic', color: '#ccc' }}>
                                Loading Session {loadingSessionId.substring(0, 8)}...
                            </Text>
                            <Button
                                appearance="subtle"
                                onClick={onCancelLoadSession}
                            >
                                Cancel
                            </Button>
                        </div>
                    ) : (
                    <>
                    <Text size={800} className={classes.welcomeTitleText}>
                        Welcome to GSC!<br/>
                        How can I help you today?
                    </Text>
                    {!isPromptsReady && (
                        <div className={isPromptsReady ? '' : classes.fadeIn} style={{ marginTop: '40px'}}>
                            <Spinner size="large" style={{ marginBottom: '20px' }} />
                            <Text style={{ fontStyle: 'italic'}}>Loading sample prompts...</Text>
                        </div>
                    )}
                    <div className={isPromptsReady ? classes.fadeIn : classes.notVisible}>
                        <WelcomeScreen
                            onButtonClick={handleSamplePromptClick} 
                            isAuthed={isAuthed} isActivision={inputSampleQueries.isActivision}
                            samplePrompts={inputSampleQueries.samplePrompts}/>
                    </div>
                    </>
                    )}
                </div>)}

                <div>
                    {effectiveAnalytics ? (
                        // Analytics mode: render chat elements with execution time and script viewer
                        settings.chat
                            .filter((msg: any) => msg.role !== 'System')
                            .map((msg: any, index: number) => (
                            <AnalyticsMessage
                                key={index}
                                msg={msg}
                                index={index}
                                onRetry={settings.isViewingOthersSession ? undefined : onRetryAnalytics}
                                retryDisabled={settings.isProcessing}
                            />
                        ))
                    ) : (
                        messagesWithLiveReasoning.map((group: ChatBundle, index: number) => (
                            <Message
                                key={index}
                                message={group}
                                onLuaScriptChange={onLuaScriptChange}
                                onSend={handleDone}
                                lastMessage={index === messagesWithLiveReasoning.length - 1}
                                hardcoded={hardcoded}
                                messageIndex={index}
                                messageSequenceIndex={indexedMessages[index]?.index ?? index}
                                isProcessing={settings.isProcessing && index === processingMessageIndex}
                                processNotificationIndex={
                                    settings.isProcessing && index === processingMessageIndex
                                        ? settings.processNotificationIndex ?? undefined
                                        : undefined
                                }
                            />
                        ))
                    )}
                    {effectiveAnalytics && settings.isProcessing && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Spinner size="medium" label="Analyzing..." />
                        </div>
                    )}

                </div>
                <div ref={chatEndRef} style={{ height: '100px' }} />
            </div>

            <Card className={classes.inputContainer}>
                {(isAuthed && !showScreen) && <div className={classes.buttonContainerStyle}>
                    <Button className={classes.buttonStyle}
                        onClick={togglePopup}
                    >
                        View Sample Queries</Button>
                    {hasDebugAccess && <Button
                        className={classes.buttonStyle}
                        icon={<DataUsageToolboxRegular />}
                        onClick={() => window.open(
                            `https://grafana-gsc-gxcph5dwdvdmbhga.cus.grafana.azure.com/d/bf390zr7lhtdsb/gsc-session-drilldown?var-Session=${encodeURIComponent(settings.sessionId || '')}`,
                            '_blank'
                        )}
                    >
                        Session Drilldown</Button>}
                    {isOpen && <QueryPopUp handleClose={togglePopup} handleButtonClick={handleSamplePromptClick}
                        samplePrompts={inputSampleQueries.samplePrompts} isActivision={inputSampleQueries.isActivision} />}

                </div>}
                {!settings.isViewingOthersSession ? (
                    <>
                        {showBanner && notificationType === 'statusBar' && (
                            <StatusBar message={bannerMsg} id={bannerId} severity={notificationSeverity} />
                        )}
                        <TextInput
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onSend={handleSend}
                            onRefresh={handleRefresh}
                            isAuthed={isAuthed}
                        />
                    </>
                ) : (
                    <div className={classes.sessionContainer}>
                        <Button icon={<Edit20Regular />} onClick={cloneSession}>
                            Clone Session
                        </Button>
                    </div>
                )}
            </Card>
        </Card>
    );
};


export default ChatUI;
